// src/pages/RepoOverviewPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import Card from "../components/Card.jsx";
import Badge from "../components/Badge.jsx";
import Modal from "../components/Modal.jsx";
import FindingsTable from "../components/FindingsTable.jsx";
import MermaidViewer from "../components/MermaidViewer.jsx";
import TrendChart from "../components/TrendChart.jsx";
import ProgressBar from "../components/ProgressBar.jsx";

import { repoApi } from "../../api/repoApi";
import { analysisApi } from "../../api/analysisApi";
import { getApiError } from "../../api/client";

import * as XLSX from "xlsx";

import "../styles/codebase.css";
import {
  Play,
  RefreshCw,
  ShieldAlert,
  Wrench,
  Network,
  FileText,
  Download,
  Activity,
  Sparkles,
  AlertTriangle,
} from "lucide-react";

/* ---------------- helpers ---------------- */

function computeProgress(scan) {
  if (!scan) return 0;
  const st = String(scan.status || "").toLowerCase();

  if (typeof scan.progress_pct === "number") {
    return Math.max(0, Math.min(100, scan.progress_pct));
  }

  if (st === "queued") return 5;

  if (st === "running") {
    const start = scan.started_at ? new Date(scan.started_at).getTime() : Date.now();
    const elapsed = Math.max(0, Date.now() - start);
    const assumedMs = 6 * 60 * 1000;
    return Math.min(95, 10 + Math.round((elapsed / assumedMs) * 85));
  }

  if (st === "completed") return 100;
  if (st === "failed") return 100;
  return 0;
}

function progressLabel(scan, pct) {
  if (!scan) return "";
  const st = String(scan.status || "").toLowerCase();

  const stage = scan.progress_stage ? String(scan.progress_stage) : "";
  if (stage && st === "running") return stage;

  if (st === "queued") return "Queued… waiting for worker";
  if (st === "running") {
    if (pct < 25) return "Cloning / preparing workspace…";
    if (pct < 45) return "Building dependency index…";
    if (pct < 65) return "Static analysis…";
    if (pct < 85) return "Security scan / Sonar…";
    return "Finalizing results…";
  }
  if (st === "completed") return "Completed";
  if (st === "failed") return scan.error ? `Failed: ${scan.error}` : "Failed";
  return "";
}

function healthTone(score) {
  if (score >= 80) return "green";
  if (score >= 60) return "blue";
  if (score >= 40) return "yellow";
  return "red";
}

function toneForSeverity(sev) {
  const s = String(sev || "").toLowerCase();
  if (s === "critical") return "red";
  if (s === "high") return "red";
  if (s === "medium") return "yellow";
  if (s === "low") return "muted";
  return "muted";
}

function toneForPriority(p) {
  const s = String(p || "").toUpperCase();
  if (s === "P0") return "red";
  if (s === "P1") return "blue";
  if (s === "P2") return "muted";
  return "muted";
}

function toneForRiskLevel(level) {
  const s = String(level || "").toLowerCase();
  if (s === "critical") return "red";
  if (s === "high") return "yellow";
  if (s === "medium") return "blue";
  if (s === "low") return "green";
  return "muted";
}

function isObj(v) {
  return v && typeof v === "object" && !Array.isArray(v);
}

function asText(v) {
  if (v === undefined || v === null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function pick(obj, keys, fallback = "") {
  if (!isObj(obj)) return fallback;
  for (const k of keys) {
    const val = obj[k];
    if (val === undefined || val === null) continue;
    if (typeof val === "string" && val.trim() === "") continue;
    return val;
  }
  return fallback;
}

function toList(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  return [v];
}

function renderEvidence(evidence) {
  const ev = toList(evidence).filter(Boolean);
  if (!ev.length) return null;

  return (
    <ul className="ai-evidence">
      {ev.slice(0, 5).map((x, i) => (
        <li key={i} className="muted small mono">
          {typeof x === "string" ? x : asText(x)}
        </li>
      ))}
    </ul>
  );
}

function renderStringList(items, max = 10) {
  const arr = toList(items).filter(Boolean);
  if (!arr.length) return null;

  return (
    <ul className="ai-list">
      {arr.slice(0, max).map((x, i) => (
        <li key={i}>{typeof x === "string" ? x : asText(x)}</li>
      ))}
    </ul>
  );
}

function renderAiInsights(ai) {
  if (!ai) return <div className="muted">No AI insights yet. Run a new scan.</div>;

  // plain text fallback (when backend JSON parse fails)
  if (typeof ai === "string") return <pre className="pre">{ai}</pre>;

  // NEW schema primary fields
  const executive = pick(ai, ["executive_summary", "summary", "overview"], "");
  const riskLevel = pick(ai, ["overall_risk_level"], "");
  const strengths = toList(ai.strengths || []);
  const gaps = toList(ai.gaps || []);
  const risks = toList(ai.top_risks || ai.risks || []);
  const recommendations = toList(ai.recommendations || []);
  const nextActions = toList(ai.next_actions || ai.actions || []);
  const roadmap = ai.roadmap || null;
  const quality = ai.quality_notes || null;

  const hasAnything =
    !!executive ||
    !!riskLevel ||
    strengths.length ||
    gaps.length ||
    risks.length ||
    recommendations.length ||
    nextActions.length ||
    !!roadmap ||
    !!quality;

  if (!hasAnything) return <pre className="pre">{asText(ai)}</pre>;

  return (
    <div className="stack">
     {/* Executive summary + risk */}
<div className="ai-block">
  <div className="ai-card-top" style={{ alignItems: "center" }}>
    <div className="ai-block-title">Executive Summary</div>
    {riskLevel ? (
      <Badge tone={toneForRiskLevel(riskLevel)}>{String(riskLevel)}</Badge>
    ) : null}
  </div>

  {!executive ? (
    <div className="muted">No executive summary returned.</div>
  ) : Array.isArray(executive) ? (
    <div className="ai-summary">{renderStringList(executive, 20)}</div>
  ) : typeof executive === "string" ? (
    <div className="ai-summary">{executive}</div>
  ) : (
    <pre className="pre">{asText(executive)}</pre>
  )}
</div>


      {/* Strengths */}
      {strengths.length > 0 && (
        <div className="ai-block">
          <div className="ai-block-title">Strengths</div>
          {renderStringList(strengths, 12)}
        </div>
      )}

      {/* Gaps */}
      {gaps.length > 0 && (
        <div className="ai-block">
          <div className="ai-block-title">Gaps</div>
          <div className="ai-cards">
            {gaps.slice(0, 10).map((g, idx) => {
              if (typeof g === "string") {
                return (
                  <div key={idx} className="ai-card">
                    <div className="ai-card-title">{g}</div>
                  </div>
                );
              }

              const priority = pick(g, ["priority"], "P2");
              const title = pick(g, ["title", "name", "gap"], "Gap");
              const description = pick(g, ["description"], "");
              const impact = pick(g, ["impact"], "");
              const evidence = pick(g, ["evidence"], "");
              const actions = toList(g.recommended_actions || []);

              return (
                <div key={idx} className="ai-card">
                  <div className="ai-card-top">
                    <div className="ai-card-title">{asText(title)}</div>
                    <Badge tone={toneForPriority(priority)}>{String(priority)}</Badge>
                  </div>

                  {description ? <div className="small">{asText(description)}</div> : null}
                  {impact ? <div className="muted small">Impact: {asText(impact)}</div> : null}
                  {evidence ? (
                    <div className="muted small mono" style={{ marginTop: 6 }}>
                      Evidence: {asText(evidence)}
                    </div>
                  ) : null}

                  {actions.length ? (
                    <div style={{ marginTop: 8 }}>
                      <div className="muted small" style={{ marginBottom: 4 }}>
                        Recommended actions:
                      </div>
                      {renderStringList(actions, 8)}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Risks */}
      {risks.length > 0 && (
        <div className="ai-block">
          <div className="ai-block-title">Top Risks</div>
          <div className="ai-cards">
            {risks.slice(0, 10).map((r, idx) => {
              if (typeof r === "string") {
                return (
                  <div key={idx} className="ai-card">
                    <div className="ai-card-title">{r}</div>
                  </div>
                );
              }

              const severity = pick(r, ["severity", "level"], "medium");
              const title = pick(r, ["title", "risk", "name"], "Risk");
              const impact = pick(r, ["impact"], "");
              const evidence = pick(r, ["evidence"], "");
              const mitigation = toList(r.mitigation || []);

              return (
                <div key={idx} className="ai-card">
                  <div className="ai-card-top">
                    <div className="ai-card-title">{asText(title)}</div>
                    <Badge tone={toneForSeverity(severity)}>{String(severity)}</Badge>
                  </div>

                  {impact ? <div className="small">{asText(impact)}</div> : null}
                  {evidence ? (
                    <div className="muted small mono" style={{ marginTop: 6 }}>
                      Evidence: {asText(evidence)}
                    </div>
                  ) : null}

                  {mitigation.length ? (
                    <div style={{ marginTop: 8 }}>
                      <div className="muted small" style={{ marginBottom: 4 }}>
                        Mitigation:
                      </div>
                      {renderStringList(mitigation, 8)}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="ai-block">
          <div className="ai-block-title">Recommendations</div>
          <div className="ai-cards">
            {recommendations.slice(0, 12).map((rec, idx) => {
              if (typeof rec === "string") {
                return (
                  <div key={idx} className="ai-card">
                    <div className="ai-card-title">{rec}</div>
                  </div>
                );
              }

              const priority = pick(rec, ["priority"], "P2");
              const title = pick(rec, ["title", "name"], "Recommendation");
              const why = pick(rec, ["why"], "");
              const steps = toList(rec.steps || []);
              const effort = pick(rec, ["effort_days"], "");
              const roi = pick(rec, ["expected_roi"], "");
              const evidence = pick(rec, ["evidence"], "");

              return (
                <div key={idx} className="ai-card">
                  <div className="ai-card-top">
                    <div className="ai-card-title">{asText(title)}</div>
                    <Badge tone={toneForPriority(priority)}>{String(priority)}</Badge>
                  </div>

                  {why ? <div className="small">{asText(why)}</div> : null}

                  <div className="muted small" style={{ marginTop: 6 }}>
                    {effort !== "" ? (
                      <>
                        Effort: <span className="mono">{String(effort)}</span> days •{" "}
                      </>
                    ) : null}
                    ROI: {roi ? asText(roi) : "—"}
                  </div>

                  {steps.length ? (
                    <div style={{ marginTop: 8 }}>
                      <div className="muted small" style={{ marginBottom: 4 }}>
                        Steps:
                      </div>
                      {renderStringList(steps, 10)}
                    </div>
                  ) : null}

                  {evidence ? (
                    <div className="muted small mono" style={{ marginTop: 6 }}>
                      Evidence: {asText(evidence)}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Roadmap */}
      {roadmap && isObj(roadmap) && (
        <div className="ai-block">
          <div className="ai-block-title">Roadmap</div>
          <div className="grid-3">
            <div className="ai-card">
              <div className="ai-card-title">0–2 weeks</div>
              {renderStringList(roadmap["0_2_weeks"] || [], 10) || <div className="muted">—</div>}
            </div>
            <div className="ai-card">
              <div className="ai-card-title">2–6 weeks</div>
              {renderStringList(roadmap["2_6_weeks"] || [], 10) || <div className="muted">—</div>}
            </div>
            <div className="ai-card">
              <div className="ai-card-title">6–12 weeks</div>
              {renderStringList(roadmap["6_12_weeks"] || [], 10) || <div className="muted">—</div>}
            </div>
          </div>
        </div>
      )}

      {/* Quality Notes */}
      {quality && isObj(quality) && (
        <div className="ai-block">
          <div className="ai-block-title">Quality Notes</div>
          <div className="ai-cards">
            {[
              ["Security", quality.security],
              ["Architecture", quality.architecture],
              ["Testing", quality.testing],
              ["CI/CD", quality.ci_cd],
              ["Observability", quality.observability],
              ["Documentation", quality.documentation],
              ["Performance", quality.performance],
            ].map(([k, v], idx) => (
              <div key={idx} className="ai-card">
                <div className="ai-card-title">{k}</div>
                <div className="muted small">{v ? asText(v) : "—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Next Actions */}
      {nextActions.length > 0 && (
        <div className="ai-block">
          <div className="ai-block-title">Next Actions</div>
          <div className="ai-cards">
            {nextActions.slice(0, 12).map((a, idx) => {
              if (typeof a === "string") {
                return (
                  <div key={idx} className="ai-card">
                    <div className="ai-card-title">{a}</div>
                  </div>
                );
              }

              const priority = pick(a, ["priority"], "P2");
              const action = pick(a, ["action", "title", "name"], "Action");
              const owner = pick(a, ["owner"], "Team");
              const effort = pick(a, ["effort_days"], "");
              const evidence = pick(a, ["evidence"], "");

              return (
                <div key={idx} className="ai-card">
                  <div className="ai-card-top">
                    <div className="ai-card-title">{asText(action)}</div>
                    <Badge tone={toneForPriority(priority)}>{String(priority)}</Badge>
                  </div>
                  <div className="muted small">
                    Owner: <span className="mono">{asText(owner)}</span>
                    {effort !== "" ? (
                      <>
                        {" "}
                        • Effort: <span className="mono">{String(effort)}</span> days
                      </>
                    ) : null}
                  </div>
                  {evidence ? (
                    <div className="muted small mono" style={{ marginTop: 6 }}>
                      Evidence: {asText(evidence)}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* Download helper: pulls ALL findings pages */
async function fetchAllFindings(lastScanId) {
  if (!lastScanId) return [];
  const pageSize = 1000;
  let offset = 0;
  let all = [];

  while (true) {
    const res = await analysisApi.getScanResults(lastScanId, { limit: pageSize, offset });
    const items = res.data?.items || [];
    all = all.concat(items);
    const total = res.data?.total || all.length;

    offset += items.length;
    if (items.length === 0 || offset >= total) break;
    if (offset > 20000) break;
  }
  return all;
}

export default function RepoOverviewPage() {
  const { repoId } = useParams();

  const [repo, setRepo] = useState(null);
  const [health, setHealth] = useState(null);
  const [trends, setTrends] = useState(null);
  const [diagram, setDiagram] = useState(null);
  const [modern, setModern] = useState(null);

  const [findings, setFindings] = useState([]);
  const [findingsTotal, setFindingsTotal] = useState(0);

  const [filters, setFilters] = useState({
    category: "",
    severity: "",
    limit: 200,
    offset: 0,
  });

  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // scan
  const [scanModal, setScanModal] = useState(false);
  const [scanForm, setScanForm] = useState({
    scan_type: "full",
    connection_token: "",
    run_sonar: true,
    sonar_token: "",
    run_ai: true,
  });
  const [scanId, setScanId] = useState(null);
  const [scanStatus, setScanStatus] = useState(null);

  const lastScanId = useMemo(
    () => health?.last_scan_id || repo?.last_scan_id || null,
    [health, repo]
  );

  const pct = computeProgress(scanStatus);
  const pctLabel = progressLabel(scanStatus, pct);

  const loadFindings = async (sid, f) => {
    const res = await analysisApi.getScanResults(sid, {
      category: f.category || undefined,
      severity: f.severity || undefined,
      limit: f.limit || 200,
      offset: f.offset || 0,
    });
    setFindings(res.data.items || []);
    setFindingsTotal(res.data.total || 0);
  };

  const loadAll = async () => {
    setErr("");
    setBusy(true);
    try {
      const [r, h, t, d, m] = await Promise.all([
        repoApi.get(repoId),
        analysisApi.getRepoHealth(repoId),
        analysisApi.getRepoTrends(repoId, 25),
        analysisApi.getDiagram(repoId),
        analysisApi.getModernization(repoId),
      ]);

      setRepo(r.data);
      setHealth(h.data);
      setTrends(t.data);
      setDiagram(d.data);
      setModern(m.data);

      const sid = h.data?.last_scan_id;
      if (sid) {
        await loadFindings(sid, filters);
      } else {
        setFindings([]);
        setFindingsTotal(0);
      }
    } catch (e) {
      setErr(getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repoId]);

  // Poll scan status when scanId is set
  useEffect(() => {
    if (!scanId) return;

    let timer = null;
    let stopped = false;

    const tick = async () => {
      try {
        const res = await analysisApi.getScan(scanId);
        if (stopped) return;
        setScanStatus(res.data);

        const st = String(res.data?.status || "").toLowerCase();
        if (st === "completed" || st === "failed") {
          if (timer) clearInterval(timer);
          timer = null;
          await loadAll();
        }
      } catch {
        // ignore polling errors
      }
    };

    tick();
    timer = setInterval(tick, 2000);

    return () => {
      stopped = true;
      if (timer) clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanId]);

  const startScan = async () => {
    setErr("");
    try {
      const res = await analysisApi.startScan({
        repo_id: Number(repoId),
        scan_type: scanForm.scan_type,
        connection_token: scanForm.connection_token || null,
        run_sonar: !!scanForm.run_sonar,
        sonar_token: scanForm.sonar_token || null,
        run_ai: !!scanForm.run_ai,
        options_json: {},
      });

      setScanId(res.data.id);
      setScanStatus(res.data);
      setScanModal(false);
    } catch (e) {
      setErr(getApiError(e));
    }
  };

  const applyFilters = async () => {
    if (!lastScanId) return;
    await loadFindings(lastScanId, filters);
  };

  const downloadExcel = async () => {
    setErr("");
    try {
      const wb = XLSX.utils.book_new();

      const repoSheet = XLSX.utils.json_to_sheet([
        {
          id: repo?.id,
          name: repo?.name,
          provider: repo?.provider,
          repo_url: repo?.repo_url,
          default_branch: repo?.default_branch,
          last_scan_id: lastScanId || "",
          last_scan_at: health?.last_scan_at || "",
          health_score: health?.health_score ?? 0,
        },
      ]);
      XLSX.utils.book_append_sheet(wb, repoSheet, "Repo");

      const counts = health?.summary?.counts || {};
      const metrics = health?.summary?.static_metrics || {};
      const sec = health?.summary?.security_metrics || {};
      const sonar = health?.summary?.sonar_metrics || {};

      const summarySheet = XLSX.utils.json_to_sheet([
        {
          findings_total: counts.findings_total ?? 0,
          critical: counts.critical ?? 0,
          high: counts.high ?? 0,
          medium: counts.medium ?? 0,
          low: counts.low ?? 0,
          loc: metrics.loc ?? 0,
          files: metrics.files ?? 0,
          complexity_score: metrics.complexity_score ?? 0,
          security_findings: sec.security_findings ?? 0,
          security_critical: sec.critical ?? 0,
          security_high: sec.high ?? 0,
          sonar_bugs: sonar.bugs ?? "",
          sonar_vulnerabilities: sonar.vulnerabilities ?? "",
          sonar_code_smells: sonar.code_smells ?? "",
        },
      ]);
      XLSX.utils.book_append_sheet(wb, summarySheet, "Summary");

      // AI sheet (high-level fields)
      const ai = health?.summary?.ai_insights;
      const aiErr = health?.summary?.ai_insights_error;
      const aiSheet = XLSX.utils.json_to_sheet([
        {
          ai_available: !!ai && !aiErr,
          ai_error: aiErr || "",
          overall_risk_level: typeof ai === "object" ? ai?.overall_risk_level || "" : "",
          executive_summary:
            typeof ai === "string"
              ? ai
              : ai?.executive_summary || ai?.summary || ai?.overview || "",
          ai_json: typeof ai === "string" ? "" : ai ? JSON.stringify(ai) : "",
        },
      ]);
      XLSX.utils.book_append_sheet(wb, aiSheet, "AI_Insights");

      const modernItems = (modern?.items || []).map((it) => ({
        priority: it.priority,
        title: it.title,
        effort_days: it.effort_days,
        expected_roi: it.expected_roi,
        details: it.details,
      }));
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(modernItems.length ? modernItems : [{ note: "No modernization items." }]),
        "Modernization"
      );

      const trendRows = (trends?.points || []).map((p) => ({
        scan_id: p.scan_id,
        at: p.at,
        findings_total: p.findings_total,
        critical: p.critical,
        high: p.high,
        complexity: p.complexity,
        loc: p.loc,
      }));
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(trendRows.length ? trendRows : [{ note: "No trend points." }]),
        "Trends"
      );

      const allFindings = await fetchAllFindings(lastScanId);
      const findingsRows = allFindings.map((f) => ({
        id: f.id,
        category: f.category,
        severity: f.severity,
        rule_id: f.rule_id,
        file_path: f.file_path,
        line: f.line,
        message: f.message,
        created_at: f.created_at,
      }));
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(findingsRows.length ? findingsRows : [{ note: "No findings." }]),
        "Findings"
      );

      const filenameSafe = (repo?.name || "repo").replace(/[^\w\-]+/g, "_");
      XLSX.writeFile(wb, `${filenameSafe}_codebase_report.xlsx`);
    } catch (e) {
      setErr(getApiError(e));
    }
  };

  const score = health?.health_score ?? 0;
  const ai = health?.summary?.ai_insights;
  const aiErr = health?.summary?.ai_insights_error;

  return (
    <div className="cb-page">
      {/* ---------- Header ---------- */}
      <div className="cb-head">
        <div className="cb-head-left">
          <h1 className="cb-title">{repo?.name || "Repository"}</h1>
          <div className="muted mono cb-url">{repo?.repo_url}</div>
          <div className="muted small">
            Provider: <span className="mono">{repo?.provider}</span> • Branch:{" "}
            <span className="mono">{repo?.default_branch || "main"}</span>
          </div>
        </div>

        <div className="cb-head-actions">
          <Link className="btn ghost" to={`/codebase/repos/${repoId}/edit`}>
            Edit
          </Link>

          <button className="btn" onClick={loadAll} disabled={busy}>
            <RefreshCw size={16} /> {busy ? "Refreshing…" : "Refresh"}
          </button>

          <button className="btn" onClick={downloadExcel} disabled={!repo}>
            <Download size={16} /> Download Excel
          </button>

          <button className="btn primary" onClick={() => setScanModal(true)}>
            <Play size={16} /> Run Scan
          </button>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      {scanStatus && (
        <div className="cb-progress">
          <ProgressBar
            title={`Scan #${scanStatus.id} • ${scanStatus.status}`}
            percent={pct}
            subtitle={pctLabel}
          />
        </div>
      )}

      {scanStatus?.status === "failed" && scanStatus?.error && (
        <div className="info">
          <b>Scan failed:</b> <span className="mono">{scanStatus.error}</span>
        </div>
      )}

      {/* ---------- KPIs ---------- */}
      <div className="grid-3">
        <Card
          title="Repo Health"
          subtitle={
            health?.last_scan_at
              ? `Last scan: ${new Date(health.last_scan_at).toLocaleString()}`
              : "No scans yet"
          }
          right={<Badge tone={healthTone(score)}>Score {score}</Badge>}
        >
          <div className="kpi-row">
            <div className="kpi">
              <div className="kpi-label">Critical</div>
              <div className="kpi-value">{health?.summary?.counts?.critical ?? 0}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">High</div>
              <div className="kpi-value">{health?.summary?.counts?.high ?? 0}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Total</div>
              <div className="kpi-value">{health?.summary?.counts?.findings_total ?? 0}</div>
            </div>
          </div>
        </Card>

        <Card title="Static Metrics" subtitle="From last scan (heuristic)">
          <div className="kpi-row">
            <div className="kpi">
              <div className="kpi-label">LOC</div>
              <div className="kpi-value">{health?.summary?.static_metrics?.loc ?? 0}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Files</div>
              <div className="kpi-value">{health?.summary?.static_metrics?.files ?? 0}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Complexity</div>
              <div className="kpi-value">{health?.summary?.static_metrics?.complexity_score ?? 0}</div>
            </div>
          </div>
        </Card>

        <Card title="Security" subtitle="Secrets + unsafe patterns">
          <div className="kpi-row">
            <div className="kpi">
              <div className="kpi-label">Findings</div>
              <div className="kpi-value">{health?.summary?.security_metrics?.security_findings ?? 0}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Critical</div>
              <div className="kpi-value">{health?.summary?.security_metrics?.critical ?? 0}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">High</div>
              <div className="kpi-value">{health?.summary?.security_metrics?.high ?? 0}</div>
            </div>
          </div>

          <div className="muted small" style={{ marginTop: 10 }}>
            Tip: Filter <b>Category = security</b> to view secrets & unsafe patterns in Findings.
          </div>
        </Card>
      </div>

      {/* ---------- Trends + Modernization ---------- */}
      <div className="grid-2 cb-section">
        <Card title="Trends" subtitle="Findings + complexity across scans" right={<Activity size={18} />}>
          <TrendChart points={trends?.points || []} />
        </Card>

        <Card
          title="Modernization Plan"
          subtitle={modern?.generated_by ? `Generated by: ${modern.generated_by}` : "Plan"}
          right={<Wrench size={18} />}
        >
          {!modern ? (
            <div className="muted">Loading…</div>
          ) : (
            <div className="stack">
              <div className="muted">{modern.summary || "—"}</div>
              {(modern.items || []).length === 0 ? (
                <div className="muted">No modernization items yet.</div>
              ) : (
                modern.items.map((it, idx) => (
                  <div key={idx} className="plan-item">
                    <div className="plan-top">
                      <div className="plan-title">{it.title}</div>
                      <Badge tone={it.priority === "P0" ? "red" : it.priority === "P1" ? "blue" : "muted"}>
                        {it.priority}
                      </Badge>
                    </div>
                    <div className="muted small">
                      Effort: <span className="mono">{it.effort_days}</span> days • ROI:{" "}
                      {it.expected_roi || "—"}
                    </div>
                    {it.details && <div className="small">{it.details}</div>}
                  </div>
                ))
              )}
            </div>
          )}
        </Card>
      </div>

      {/* ---------- AI Insights ---------- */}
      <div className="grid-2 cb-section">
        <Card
          title="AI Insights (Llama)"
          subtitle="Comprehensive AI report from scan summary + findings sample"
          right={<Sparkles size={18} />}
        >
          {aiErr ? (
            <div className="info">
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <AlertTriangle size={16} />
                <b>AI Insights not available</b>
              </div>
              <div className="mono small" style={{ marginTop: 6 }}>
                {String(aiErr)}
              </div>
              <div className="muted small" style={{ marginTop: 8 }}>
                Run a new scan after enabling AI in backend.
              </div>
            </div>
          ) : (
            renderAiInsights(ai)
          )}
        </Card>

        <Card title="AI + Engineering Notes" subtitle="Next actions / suggested plan">
          {aiErr ? (
            <div className="muted">AI is not available for this scan.</div>
          ) : ai ? (
            (() => {
              const obj = typeof ai === "object" ? ai : null;
              const next = obj ? (obj.next_actions || obj.actions || []) : [];
              const roadmap = obj ? obj.roadmap : null;
              const q = obj ? obj.quality_notes : null;

              return (
                <div className="stack">
                  {Array.isArray(next) && next.length > 0 ? (
                    <div className="ai-block">
                      <div className="ai-block-title">Next Actions</div>
                      <div className="ai-cards">
                        {next.slice(0, 12).map((a, idx) => {
                          if (typeof a === "string") {
                            return (
                              <div key={idx} className="ai-card">
                                <div className="ai-card-title">{a}</div>
                              </div>
                            );
                          }
                          return (
                            <div key={idx} className="ai-card">
                              <div className="ai-card-top">
                                <div className="ai-card-title">{asText(a.action || a.title || "Action")}</div>
                                <Badge tone={toneForPriority(a.priority || "P2")}>{String(a.priority || "P2")}</Badge>
                              </div>
                              <div className="muted small">
                                Owner: <span className="mono">{asText(a.owner || "Team")}</span>
                                {a.effort_days !== undefined ? (
                                  <>
                                    {" "}
                                    • Effort: <span className="mono">{String(a.effort_days)}</span> days
                                  </>
                                ) : null}
                              </div>
                              {a.evidence ? (
                                <div className="muted small mono" style={{ marginTop: 6 }}>
                                  Evidence: {asText(a.evidence)}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}

                  {roadmap && typeof roadmap === "object" ? (
                    <div className="ai-block">
                      <div className="ai-block-title">Roadmap</div>
                      <pre className="pre">{asText(roadmap)}</pre>
                    </div>
                  ) : null}

                  {q && typeof q === "object" ? (
                    <div className="ai-block">
                      <div className="ai-block-title">Quality Notes</div>
                      <pre className="pre">{asText(q)}</pre>
                    </div>
                  ) : null}

                  {(!next || next.length === 0) && !roadmap && !q ? (
                    <div className="muted">
                      Run a new scan after backend prompt update. This scan doesn't include next_actions / roadmap / quality_notes yet.
                    </div>
                  ) : null}
                </div>
              );
            })()
          ) : (
            <div className="muted">No AI insights yet. Run a new scan.</div>
          )}
        </Card>
      </div>

      {/* ---------- Diagram + Findings ---------- */}
      <div className="grid-2 cb-section">
        <Card title="Architecture Diagram" subtitle="Auto-generated Mermaid" right={<Network size={18} />}>
          <MermaidViewer code={diagram?.mermaid} />
        </Card>

        <Card
          title="Findings"
          subtitle={`From last scan • Total: ${findingsTotal}`}
          right={<ShieldAlert size={18} />}
        >
          {!lastScanId ? (
            <div className="muted">
              No scan results yet. Click <b>Run Scan</b> to generate findings.
            </div>
          ) : (
            <div className="stack">
              <div className="filters">
                <div className="field">
                  <label>Category</label>
                  <select
                    value={filters.category}
                    onChange={(e) => setFilters((p) => ({ ...p, category: e.target.value }))}
                  >
                    <option value="">All</option>
                    <option value="security">security</option>
                    <option value="sonar">sonar</option>
                    <option value="smell">smell</option>
                    <option value="duplication">duplication</option>
                    <option value="complexity">complexity</option>
                  </select>
                </div>

                <div className="field">
                  <label>Severity</label>
                  <select
                    value={filters.severity}
                    onChange={(e) => setFilters((p) => ({ ...p, severity: e.target.value }))}
                  >
                    <option value="">All</option>
                    <option value="critical">critical</option>
                    <option value="high">high</option>
                    <option value="medium">medium</option>
                    <option value="low">low</option>
                    <option value="info">info</option>
                  </select>
                </div>

                <div className="field">
                  <label>Limit</label>
                  <input
                    type="number"
                    value={filters.limit}
                    onChange={(e) => setFilters((p) => ({ ...p, limit: Number(e.target.value) || 200 }))}
                  />
                </div>

                <div className="field">
                  <label>&nbsp;</label>
                  <button className="btn" onClick={applyFilters}>
                    <FileText size={16} /> Apply
                  </button>
                </div>
              </div>

              <FindingsTable items={findings} />
            </div>
          )}
        </Card>
      </div>

      {/* ---------- Scan Modal ---------- */}
      <Modal
        open={scanModal}
        title="Run Scan"
        onClose={() => setScanModal(false)}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button className="btn ghost" onClick={() => setScanModal(false)}>
              Cancel
            </button>
            <button className="btn primary" onClick={startScan}>
              Start Scan
            </button>
          </div>
        }
      >
        <div className="form">
          <div className="grid-2">
            <div className="field">
              <label>Scan Type</label>
              <select
                value={scanForm.scan_type}
                onChange={(e) => setScanForm((p) => ({ ...p, scan_type: e.target.value }))}
              >
                <option value="full">full</option>
                <option value="incremental">incremental</option>
                <option value="sonar_only">sonar_only</option>
              </select>
            </div>

            <div className="field">
              <label>Run Sonar</label>
              <select
                value={scanForm.run_sonar ? "yes" : "no"}
                onChange={(e) => setScanForm((p) => ({ ...p, run_sonar: e.target.value === "yes" }))}
              >
                <option value="yes">yes</option>
                <option value="no">no</option>
              </select>
            </div>
          </div>

          <div className="field">
            <label>AI Insights (Llama)</label>
            <select
              value={scanForm.run_ai ? "yes" : "no"}
              onChange={(e) => setScanForm((p) => ({ ...p, run_ai: e.target.value === "yes" }))}
            >
              <option value="yes">yes</option>
              <option value="no">no</option>
            </select>
            <div className="hint">Generates executive summary + risks + gaps + recommendations + next actions.</div>
          </div>

          <div className="field">
            <label>Connection Token (PAT/OAuth)</label>
            <input
              value={scanForm.connection_token}
              onChange={(e) => setScanForm((p) => ({ ...p, connection_token: e.target.value }))}
              placeholder="Not stored. Used only for this scan."
            />
            <div className="hint">For GitHub/Azure DevOps Git clone, provide PAT/OAuth token here.</div>
          </div>

          <div className="field">
            <label>Sonar Token (optional)</label>
            <input
              value={scanForm.sonar_token}
              onChange={(e) => setScanForm((p) => ({ ...p, sonar_token: e.target.value }))}
              placeholder="Not stored. Used only for this scan."
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
